import { Box, Stack } from '@chakra-ui/react';
import type React from 'react';
import FaceliftText from '@/components/Atoms/FaceliftText';
import Show from '@/components/Molecules/Show';
import DetailsView from '@/components/Templates/DetailsView';
import type { DenialSupportDetailsResponse } from '@/types/denialSupport';
import { formatDate } from '@/utils';
import { mapStatus } from '../constants';

interface DenialSupportDetailsProps {
	data: DenialSupportDetailsResponse | null;
	isLoading?: boolean;
	onRemoveDocument?: (documentId: string) => void;
}

const DenialSupportDetails: React.FC<DenialSupportDetailsProps> = ({
	data,
	isLoading = false,
}) => {
	if (isLoading || !data) {
		return (
			<Box p={4}>
				<FaceliftText>{isLoading ? 'Loading...' : 'No data available'}</FaceliftText>
			</Box>
		);
	}

	const appealStatus = mapStatus(data.ta_appealstatus);
	const products = data.products || [];

	const getShipDate = (inventory?: typeof data.inventory) => {
		if (!inventory) return '-';
		if (
			import.meta.env.VITE_ENV === 'PREPROD' ||
			import.meta.env.VITE_ENV === 'PROD' || import.meta.env.VITE_ENV === 'prod'
		) {
			return inventory.ta_estimateddeliverydate
				? formatDate(inventory.ta_estimateddeliverydate)
				: '-';
		}
		return inventory.ta_calculatedestimateddeliverydate
			? formatDate(inventory.ta_calculatedestimateddeliverydate)
			: '-';
	};

	// General information section
	const generalInfo = [
		{ label: 'Name', value: data.ta_name || '-' },
		{ label: 'Clinic', value: data.clinic?.name || '-' },
		{ label: 'Patient', value: data.patient?.fullname || '-' },
		{
			label: 'Denial Date',
			value: data.ta_denial_date ? formatDate(data.ta_denial_date) : '-',
		},
		{ label: 'Created On', value: formatDate(data.createdon) || '-' },
		{
			label: 'Amount Billed',
			value: `$${data.ta_amountbilled?.toFixed(2) || '0.00'}`,
		},
		{ label: 'Appeal Status', value: appealStatus.label },
		{
			label: 'Appeal Attempt',
			value: data.ta_appealattempt ? String(data.ta_appealattempt) : '-',
		},
		{ label: 'Origin Source', value: data.ta_originationsource || '-' },
	];

	return (
		<Box maxH="80vh" overflowY="auto">
			<Box px={2}>
				<Stack gap={4}>
					{/* General Information Section */}
					<Box px={2}>
						<DetailsView
							data={[{ data: generalInfo }]}
							gridConfig={{ base: '1fr', md: '1fr' }}
							gap={{ base: 3, md: 3 }}
						/>
					</Box>

					{/* Products Section */}
					<Show when={products.length > 0}>
						{products.map((product, index) => {
							const inventory = product.inventory;
							const productData = [
								{
									label: 'Product Name',
									value: product.ta_name || '-',
								},
								{
									label: 'Inventory ID',
									value: inventory?.ta_name || '-',
								},
								{
									label: 'Tissue ID',
									value: inventory?.ta_tissueid || '-',
								},
								{
									label: 'Invoice Due Date',
									value: inventory?.ta_invoiceduedate
										? formatDate(inventory.ta_invoiceduedate)
										: '-',
								},
								{
									label: 'Manufacturer',
									value: inventory?.ta_manufacturer || '-',
								},
								{
									label: 'Quantity',
									value: inventory?.ta_units ? String(inventory.ta_units) : '-',
								},
								{
									label: 'Request Date',
									value: inventory?.createdon
										? formatDate(inventory.createdon)
										: '-',
								},
								{
									label: 'Ship Date',
									value: getShipDate(inventory),
								},
							];

							return (
								<Box key={index} px={2} mt={index === 0 ? 8 : 6}>
									<FaceliftText
										fontSize="lg"
										fontWeight="bold"
										mb={4}
									>
										{products.length > 1
											? `Product ${index + 1}`
											: 'Product'}
									</FaceliftText>

									<DetailsView
										data={[{ data: productData }]}
										gridConfig={{ base: '1fr', md: '1fr' }}
										gap={{ base: 3, md: 3 }}
									/>
								</Box>
							);
						})}
					</Show>

					{/* Fallback for single inventory */}
					<Show when={products.length === 0 && !!data.inventory}>
						<Box px={2} mt={8}>
							<FaceliftText
								fontSize="lg"
								fontWeight="bold"
								mb={4}
							>
								Product
							</FaceliftText>

							<DetailsView
								data={[
									{
										data: [
											{
												label: 'Product Name',
												value: data.inventory?.product?.ta_name || '-',
											},
											{
												label: 'Inventory ID',
												value: data.inventory?.ta_name || '-',
											},
											{
												label: 'Tissue ID',
												value: data.inventory?.ta_tissueid || '-',
											},
											{
												label: 'Invoice Due Date',
												value: data.inventory?.ta_invoiceduedate
													? formatDate(data.inventory.ta_invoiceduedate)
													: '-',
											},
											{
												label: 'Manufacturer',
												value: data.inventory?.ta_manufacturer || '-',
											},
											{
												label: 'Quantity',
												value: data.inventory?.ta_units
													? String(data.inventory.ta_units)
													: '-',
											},
											{
												label: 'Request Date',
												value: data.inventory?.createdon
													? formatDate(data.inventory.createdon)
													: '-',
											},
											{
												label: 'Ship Date',
												value: getShipDate(data.inventory),
											},
										],
									},
								]}
								gridConfig={{ base: '1fr', md: '1fr' }}
								gap={{ base: 3, md: 3 }}
							/>
						</Box>
					</Show>
				</Stack>
			</Box>
		</Box>
	);
};

export default DenialSupportDetails;
